import { OlympCaptchaDto } from '@/Olymp/types';
import { UseRequest, useRequest } from '@/hooks/useRequest';
import { createContext, Dispatch, SetStateAction, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { UseBoolean, useBoolean } from '@/hooks/useBoolean';
import { useValueRef } from '@/hooks/useValueRef';
import { publicRuntimeConfig } from '@repo/config';
import {
  LoginTradingCoreUrl,
  RegisterTradingCoreUrl,
  RestorePasswordTradingCoreUrl,
  GetUserTradingCoreUrl,
  CaptchaTradingCoreUrl,
  getRTCHTTPHeaders,
  RequestMethods,
  RequestStatuses,
  redirectToApp,
  AnalyticClient,
  BaseUserDto,
} from '@repo/utils';
import i18next from 'i18next';

export enum AuthTabsValues {
  Login = 'login',
  Register = 'register',
}

interface MeState {
  fetched: boolean;
  value?: BaseUserDto;
}

type CaptchaState = {
  fetched: boolean;
  timestamp?: number;
  state?: OlympCaptchaDto;
};

interface Context {
  meState: MeState;
  captchaState: CaptchaState;
  tabState: [AuthTabsValues, Dispatch<SetStateAction<AuthTabsValues>>];
  opened: UseBoolean;
  onLogin: (args: { email: string; password: string; recaptchaValue?: string }) => void;
  onRegister: (args: { email: string; password: string; ad_id: string; recaptchaValue?: string }) => void;
  onRestorePassword: (args: { email: string; recaptchaValue?: string }) => void;
  getCheckCaptcha: () => void;
  LoginRequest: UseRequest;
  RegisterRequest: UseRequest;
  RestorePasswordRequest: UseRequest;
}

const NEGATIVE_STATUSES = [RequestStatuses.Unauthorized, RequestStatuses.Failed];

const AuthContext = createContext({} as Context);

export const useAuthContext = () => useContext(AuthContext);

const AuthContextProvider = AuthContext.Provider;

interface Props {
  children?: any;
  defaultOpen?: boolean;
}

export const AuthProvider = (props: Props) => {
  const { children } = props;
  const [meState, setMeState] = useState<MeState>({
    fetched: false,
    value: undefined,
  });
  const [captchaState, setCaptchaState] = useState<Context['captchaState']>({
    fetched: false,
  });
  const userIdRef = useValueRef(meState.value?.traderId);
  const opened = useBoolean(Boolean(props.defaultOpen));
  const tabState = useState<AuthTabsValues>(AuthTabsValues.Register);
  const CaptchaRequest = useRequest({
    url: CaptchaTradingCoreUrl,
    method: RequestMethods.Get,
    headers: getRTCHTTPHeaders(),
  });

  const LoginRequest = useRequest({
    url: LoginTradingCoreUrl,
    method: RequestMethods.Post,
    trackError: false,
    headers: getRTCHTTPHeaders(),
  });

  const RegisterRequest = useRequest({
    url: RegisterTradingCoreUrl,
    method: RequestMethods.Post,
    trackError: false,
    headers: getRTCHTTPHeaders(),
  });

  const RestorePasswordRequest = useRequest({
    url: RestorePasswordTradingCoreUrl,
    method: RequestMethods.Post,
    headers: getRTCHTTPHeaders(),
  });

  const GetUserRequest = useRequest({
    url: GetUserTradingCoreUrl,
    method: RequestMethods.Get,
    retryOnUnauthorized: true,
    trackError: false,
    headers: getRTCHTTPHeaders(),
  });

  const log = useCallback((...d: any[]) => {
    if (publicRuntimeConfig.app.log) {
      console.log('[AuthContext]', ...d);
    }
  }, []);

  const handleRegister = useCallback<Context['onRegister']>(({ email, password, recaptchaValue, ad_id }) => {
    RegisterRequest.onRequest({
      data: { email, password, recaptchaValue, ad_id, appVersion: AnalyticClient.getWebAppVersion() },
    });
  }, []);

  const handleLogin = useCallback<Context['onLogin']>(({ email, password, recaptchaValue }) => {
    LoginRequest.onRequest({
      data: { email, password, recaptchaValue },
    });
  }, []);

  const handleRestorePassword = useCallback<Context['onRestorePassword']>(({ email, recaptchaValue }) => {
    RestorePasswordRequest.onRequest({
      data: { email, recaptchaValue },
    });
  }, []);

  const handleGetUser = useCallback(async () => {
    await GetUserRequest.onRequest({
      data: {},
    });
  }, []);

  const getCheckCaptcha = useCallback<Context['getCheckCaptcha']>(() => {
    CaptchaRequest.onRequest();
  }, []);

  const onLocationChanged = useCallback(() => {
    if (Boolean(userIdRef.current) && window.location.pathname === '/') {
      redirectToApp();
    }
  }, []);

  useEffect(() => {
    handleGetUser();
  }, []);

  useEffect(() => {
    const { pushState, replaceState } = window.history;

    window.history.pushState = function (...args) {
      pushState.apply(window.history, args);
      window.dispatchEvent(new Event('pushState'));
    };

    window.history.replaceState = function (...args) {
      replaceState.apply(window.history, args);
      window.dispatchEvent(new Event('replaceState'));
    };

    window.addEventListener('popstate', onLocationChanged);
    window.addEventListener('replaceState', onLocationChanged);
    window.addEventListener('pushState', onLocationChanged);
  }, []);

  useEffect(() => {
    if (meState.fetched && Boolean(meState.value?.traderId) && (window.location.pathname === '/' || opened.value)) {
      redirectToApp();
    }
  }, [meState.fetched, meState.value, opened.value]);

  useEffect(() => {
    if (GetUserRequest.state.status === RequestStatuses.Succeeded) {
      setMeState({
        fetched: true,
        value: GetUserRequest.state.result.payload?.user,
      });
    } else if (NEGATIVE_STATUSES.includes(GetUserRequest.state.status)) {
      setMeState({
        fetched: true,
      });
    }
  }, [GetUserRequest.state.status]);

  useEffect(() => {
    if (NEGATIVE_STATUSES.includes(LoginRequest.state.status)) {
      setMeState({
        fetched: true,
      });
    }
  }, [LoginRequest.state.status]);

  useEffect(() => {
    if (NEGATIVE_STATUSES.includes(RegisterRequest.state.status)) {
      setMeState({
        fetched: true,
      });
    }
  }, [RegisterRequest.state.status]);

  useEffect(() => {
    if (!meState.fetched || Boolean(meState.value?.traderId)) {
      return;
    }
    CaptchaRequest.onRequest();
  }, [meState.fetched, meState.value?.traderId]);

  useEffect(() => {
    if (CaptchaRequest.state.status === RequestStatuses.Succeeded) {
      log('[AuthContext] set captcha state');
      setCaptchaState({
        fetched: true,
        timestamp: Date.now(),
        state: CaptchaRequest.state.result.payload.captcha,
      });
    }
  }, [CaptchaRequest.state.status]);

  useEffect(() => {
    window.AuthModule = window.AuthModule || {};
    window.AuthModule.onOpen = () => {
      opened.onTrue();
    };
    window.AuthModule.onClose = () => {
      opened.onFalse();
    };
    window.AuthModule.onToggle = () => {
      opened.onToggle();
    };
    window.AuthModule.onChangeLanguage = (ln: string) => {
      i18next.changeLanguage(ln);
      AnalyticClient.onChangeLanguage(ln);
    };
  }, []);

  const contextValue = useMemo(
    () => ({
      meState,
      captchaState,
      tabState,
      opened,
      onLogin: handleLogin,
      onRegister: handleRegister,
      onRestorePassword: handleRestorePassword,
      onGetUser: handleGetUser,
      getCheckCaptcha,
      LoginRequest,
      RegisterRequest,
      RestorePasswordRequest,
    }),
    [
      meState,
      captchaState,
      tabState,
      opened,
      handleLogin,
      handleRegister,
      handleRestorePassword,
      handleGetUser,
      getCheckCaptcha,
      LoginRequest,
      RegisterRequest,
      RestorePasswordRequest,
    ],
  );

  return <AuthContextProvider value={contextValue}>{children}</AuthContextProvider>;
};
